import { makeObservable, observable, action } from 'mobx'
import type {
  MicContext,
  MicFailure,
  MicReport,
  SpeechDetection,
} from './types'

/**
 * The probe MicCheck reads to decide ready vs digital_silence, and whether a
 * live mic is loud enough to hear (near_silence).
 */
export type MicMeasurement = {
  // True when the current window carries no signal at all.
  isSilent: () => boolean
  // The current window's loudness, RMS on the 0-1 full-scale range.
  readLevel: () => number
  cleanup: () => void
}

// 1s: a quiet room's hiss is steady, so one analyser window a second shows the
// noise floor; speech is far louder than the threshold.
const LEVEL_SAMPLE_INTERVAL_MS = 1000

/**
 * When silence counts as a failure.
 *
 * - continuous: a preview (the AV check, the device picker), where the student
 *   is asked to speak, so silence at any moment is a fault.
 * - capture_start: the meeting, where a student who is listening is silent.
 *   On 2026-10-13, after 240 of 245 meeting silence warnings the worker heard
 *   the same student speak again in the same room. Only the first unmuted
 *   seconds of each new capture are read for digital zeros, and near_silence
 *   never runs.
 */
export type SilenceCheck = 'continuous' | 'capture_start'

// Digital-zero reads in a row that confirm digital_silence. A capture start
// gets one chance, so it takes one more read than a preview, which keeps
// re-reading.
const SILENT_READS_TO_FAIL: Record<SilenceCheck, number> = {
  continuous: 2,
  capture_start: 3,
}

export type SpeechDetectorCleanup = () => void | Promise<void>

/**
 * Everything MicCheck needs from the outside, injected so the protocol can be
 * driven by a fake track and a stubbed worklet loader in tests, and by the real
 * analysers in the browser.
 */
export type MicCheckDeps = {
  // The digital-zero probe over the raw track. Throws only when the audio
  // context is unsupported.
  createMeasurement: (track: MediaStreamTrack) => MicMeasurement
  // The worklet speech detector. Resolves with a cleanup, or rejects when the
  // worklet fails to load — which sets speechDetection: 'unavailable', never a
  // silent microphone.
  createSpeechDetector: (
    track: MediaStreamTrack,
    onThresholdCross: (over: boolean) => void
  ) => Promise<SpeechDetectorCleanup>
  // Microphones, by deviceId, that have been loud this page session. Shared
  // by the AV check and the device picker, so a mic proven in one is trusted
  // in the other. Only near_silence reads it, and meetings never check that.
  provenDevices: Set<string>
  silenceCheck: SilenceCheck
  // Calls the listener when the shared audio context stops processing
  // (suspended, interrupted or closed: false) or starts again (true). The
  // speech detector goes quiet while it is stopped without reporting the end
  // of speech. Returns an unsubscribe.
  onAudioRunningChange: (listener: (running: boolean) => void) => () => void
  now: () => number
  setTimer: (fn: () => void, ms: number) => ReturnType<typeof setTimeout>
  clearTimer: (handle: ReturnType<typeof setTimeout>) => void
  config: () => {
    micGraceMS: number
    // How long a track that has never appeared may stay missing before
    // no_track. The meeting mic publishes seconds after joining; a preview
    // track is handed over already captured.
    publishGraceMS: number
    silenceConfirmDelayMS: number
    silencePollIntervalMS: number
    nearSilenceRMS: number
    nearSilenceMS: number
  }
}

type CheckingReason =
  | 'starting'
  | 'changing_device'
  | 'reconnecting'
  | 'repairing'

/**
 * Whether two reports are identical — used to suppress redundant emits. Compares
 * per variant with `in`-narrowing (no casts, no reliance on object key order).
 */
function reportsEqual(a: MicReport, b: MicReport): boolean {
  if (a.state !== b.state) return false
  switch (a.state) {
    case 'inactive':
    case 'checking':
      return 'reason' in b && a.reason === b.reason
    case 'ready':
      return (
        'hasHeardSpeech' in b &&
        a.hasHeardSpeech === b.hasHeardSpeech &&
        a.speechDetection === b.speechDetection
      )
    case 'failed':
      return 'cause' in b && a.cause === b.cause
  }
}

/**
 * Says what the microphone is doing, and nothing else. It is told why audio is
 * on or off (`setContext`) and which underlying track to observe (`setTrack`);
 * it never reads MeetingCubit, never looks at slides, and never guesses intent
 * from a disabled track. Every new track is a new observation, and a generation
 * number rejects late asynchronous work from a previous one.
 */
export class MicCheck {
  report: MicReport = { state: 'checking', reason: 'starting' }

  private readonly deps: MicCheckDeps
  private context: MicContext = 'active'
  private track: MediaStreamTrack | null = null
  // Bumped on every input change so late async work (a worklet load, a read
  // callback) from a previous observation can recognise itself as stale.
  private generation = 0
  private disposed = false
  private readonly unsubscribeAudioRunningChange: () => void

  private measuring = false
  private measurement: MicMeasurement | null = null
  private speechCleanup: SpeechDetectorCleanup | null = null
  private speechDetection: SpeechDetection = 'available'
  private hasHeardSpeech = false
  private silentReadsInRow = 0
  // capture_start only: the current capture has made sound, so it is never
  // read for silence again.
  private captureMadeSound = false
  // Local speech on the current capture while it is checked, for the meeting's
  // delivery check. The speech in progress started at speechStartedAt.
  private speechMS = 0
  private speechStartedAt: number | null = null
  // The audio context stopped while the student was speaking, and the
  // detector has not reported the end of that speech since.
  private speechCutByStop = false
  private pendingRecoveryConfirm = false
  // When the level last dropped under nearSilenceRMS and stayed there; null
  // while the mic is loud enough.
  private quietSince: number | null = null
  private loudSamplesInRow = 0
  // Empty when the browser does not report one; the proof then holds for the
  // current track only.
  private deviceId = ''
  private trackProven = false
  private publishRejected = false
  private checkingReason: CheckingReason = 'starting'

  private graceTimer: ReturnType<typeof setTimeout> | null = null
  private pollTimer: ReturnType<typeof setTimeout> | null = null
  private levelTimer: ReturnType<typeof setTimeout> | null = null

  constructor(deps: MicCheckDeps) {
    this.deps = deps
    this.unsubscribeAudioRunningChange = deps.onAudioRunningChange((running) =>
      this.onAudioRunningChange(running)
    )
    makeObservable<MicCheck, 'setReport'>(this, {
      report: observable.ref,
      setReport: action,
    })
    this.evaluate()
  }

  setContext(context: MicContext): void {
    if (this.disposed || context === this.context) return
    this.generation++
    this.context = context
    this.evaluate()
  }

  /** A new track starts a new observation. */
  setTrack(track: MediaStreamTrack | null): void {
    if (this.disposed) return
    this.generation++
    const hadTrack = this.track !== null
    this.teardownMeasurement()
    this.clearGraceTimer()
    this.track = track
    this.deviceId = track?.getSettings().deviceId ?? ''
    this.trackProven = false
    this.publishRejected = false
    this.hasHeardSpeech = false
    this.speechDetection = 'available'
    this.silentReadsInRow = 0
    this.pendingRecoveryConfirm = false
    this.captureMadeSound = false
    this.speechMS = 0
    this.speechStartedAt = null
    this.checkingReason = hadTrack ? 'changing_device' : 'starting'
    this.evaluate()
  }

  /**
   * Milliseconds the speech detector has heard speech on the current track
   * while it was checked (unmuted, in an active context), counting speech in
   * progress: a student who talks without pausing has still talked.
   */
  localSpeechMS(): number {
    const inProgress =
      this.speechStartedAt === null ? 0 : this.deps.now() - this.speechStartedAt
    return this.speechMS + inProgress
  }

  /** A rejected publish is no_track at once, no grace. */
  reportPublishFailed(): void {
    if (this.disposed) return
    this.generation++
    this.teardownMeasurement()
    this.clearGraceTimer()
    this.track = null
    this.publishRejected = true
    this.evaluate()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.generation++
    this.unsubscribeAudioRunningChange()
    this.teardownMeasurement()
    this.clearGraceTimer()
  }

  private setReport(report: MicReport): void {
    // Only churn the observable on a genuine change. A poll read re-derives the
    // same `ready` report every interval; without this, the observable (and so
    // the reaction that logs soundcheck_mic_result, and the UI) would fire on
    // every tick instead of on state changes.
    if (reportsEqual(this.report, report)) return
    this.report = report
  }

  private classifyUnusable(): MicFailure | null {
    if (this.track === null) return 'no_track'
    if (this.track.readyState === 'ended') return 'track_stopped'
    // The SDK disabled the publication without the user asking; user intent
    // arrives as context 'user_muted', never as a disabled track.
    if (!this.track.enabled) return 'track_stopped'
    return null
  }

  private evaluate(): void {
    if (this.disposed) return

    switch (this.context) {
      case 'user_muted':
        this.teardownMeasurement()
        this.clearGraceTimer()
        this.setReport({ state: 'inactive', reason: 'user_muted' })
        return
      case 'not_required':
        this.teardownMeasurement()
        this.clearGraceTimer()
        this.setReport({ state: 'inactive', reason: 'not_required' })
        return
      case 'reconnecting':
        this.teardownMeasurement()
        this.clearGraceTimer()
        this.setReport({ state: 'checking', reason: 'reconnecting' })
        return
      case 'repairing':
        this.teardownMeasurement()
        this.clearGraceTimer()
        this.setReport({ state: 'checking', reason: 'repairing' })
        return
      case 'active':
        break
    }

    if (this.publishRejected) {
      this.teardownMeasurement()
      this.clearGraceTimer()
      this.setReport({ state: 'failed', cause: 'no_track' })
      return
    }

    const unusable = this.classifyUnusable()
    if (unusable === null) {
      // A live, enabled track: measure it. Measurement keeps running through a
      // digital_silence failure so a later non-zero read can recover.
      this.clearGraceTimer()
      this.ensureMeasuring()
      return
    }

    // A dead track cannot be measured for readiness. Give it grace to start or
    // restart, then confirm the failure.
    this.teardownMeasurement()
    this.startGraceIfNeeded(unusable)
  }

  private startGraceIfNeeded(cause: MicFailure): void {
    if (this.report.state === 'failed' && this.report.cause === cause) return
    if (this.graceTimer !== null) return
    this.setReport({ state: 'checking', reason: this.checkingReason })
    const config = this.deps.config()
    const awaitingFirstTrack =
      cause === 'no_track' && this.checkingReason === 'starting'
    const graceMS = awaitingFirstTrack
      ? config.publishGraceMS
      : config.micGraceMS
    this.graceTimer = this.deps.setTimer(() => {
      this.graceTimer = null
      if (this.disposed || this.context !== 'active') {
        this.evaluate()
        return
      }
      const stillUnusable = this.classifyUnusable()
      if (this.publishRejected) {
        this.setReport({ state: 'failed', cause: 'no_track' })
      } else if (stillUnusable) {
        this.setReport({ state: 'failed', cause: stillUnusable })
      } else {
        this.evaluate()
      }
    }, graceMS)
  }

  private ensureMeasuring(): void {
    if (this.measuring || this.track === null) return
    this.measuring = true
    const track = this.track
    const gen = this.generation

    const silenceAlreadyChecked =
      this.deps.silenceCheck === 'capture_start' && this.captureMadeSound
    if (silenceAlreadyChecked) {
      // Unmuting a meeting capture that has made sound: silence from here on
      // is a student listening, so there is nothing to read.
      this.setReadyReport()
    } else {
      try {
        this.measurement = this.deps.createMeasurement(track)
      } catch {
        // No probe means we cannot prove silence; treat the track as ready
        // with speech detection unavailable rather than manufacture a failure.
        this.measurement = null
        this.speechDetection = 'unavailable'
        this.setReadyReport()
      }
    }

    this.deps
      .createSpeechDetector(track, (over) => this.onThresholdCross(gen, over))
      .then((cleanup) => {
        if (gen !== this.generation) {
          void cleanup()
          return
        }
        this.speechCleanup = cleanup
        this.speechDetection = 'available'
        this.refreshReadyReport()
      })
      .catch(() => {
        if (gen !== this.generation) return
        this.speechDetection = 'unavailable'
        this.refreshReadyReport()
      })

    if (this.report.state !== 'ready') {
      this.setReport({ state: 'checking', reason: this.checkingReason })
    }
    if (this.measurement) {
      // Let the mic warm up before the first read; the analyser buffer starts at
      // the zero line and would read silent immediately.
      this.scheduleRead(this.deps.config().micGraceMS)
      if (this.deps.silenceCheck === 'continuous') this.scheduleLevelSample()
    }
  }

  private scheduleRead(delay: number): void {
    if (this.pollTimer !== null) this.deps.clearTimer(this.pollTimer)
    this.pollTimer = this.deps.setTimer(() => {
      this.pollTimer = null
      this.performRead()
    }, delay)
  }

  private performRead(): void {
    if (this.disposed || !this.measuring || this.measurement === null) return
    const { silenceConfirmDelayMS, silencePollIntervalMS } = this.deps.config()
    const silent = this.measurement.isSilent()
    if (!silent) {
      this.silentReadsInRow = 0
      // A near-silent mic reads non-zero; only the level sampling may clear it.
      if (this.isFailedWith('near_silence')) {
        this.scheduleRead(silencePollIntervalMS)
        return
      }
      const recovering = this.isFailedWith('digital_silence')
      if (recovering && !this.pendingRecoveryConfirm) {
        // One non-zero read is not recovery: a dead mic that leaks a single
        // blip flipped failed/ready four times in one session. Confirm it.
        this.pendingRecoveryConfirm = true
        this.scheduleRead(silenceConfirmDelayMS)
        return
      }
      this.pendingRecoveryConfirm = false
      this.setReadyReport()
      if (this.deps.silenceCheck === 'capture_start') {
        // This capture works. Stop reading: later silence is listening.
        this.captureMadeSound = true
        return
      }
      this.scheduleRead(silencePollIntervalMS)
      return
    }
    this.pendingRecoveryConfirm = false
    this.silentReadsInRow++
    const confirmed =
      this.silentReadsInRow >= SILENT_READS_TO_FAIL[this.deps.silenceCheck]
    if (!confirmed) {
      // Keep the current report and take another read.
      this.scheduleRead(silenceConfirmDelayMS)
      return
    }
    this.setReport({ state: 'failed', cause: 'digital_silence' })
    // Keep polling: a mic that comes back non-zero recovers.
    this.scheduleRead(silencePollIntervalMS)
  }

  private scheduleLevelSample(): void {
    if (this.levelTimer !== null) this.deps.clearTimer(this.levelTimer)
    this.levelTimer = this.deps.setTimer(() => {
      this.levelTimer = null
      this.sampleLevel()
    }, LEVEL_SAMPLE_INTERVAL_MS)
  }

  /**
   * A live mic that never gets louder than a faint hiss for nearSilenceMS is
   * near_silence: it passes the digital-zero test, so it read ready while the
   * server received packets at maxRMS 0.0001 for a whole session. Recovery
   * takes two loud samples in a row, like the other reads.
   *
   * Two loud samples also prove the device, and a proven device is never
   * near_silence again: a quiet stretch on a mic that has worked is a student
   * listening. On 2026-10-04, 1,721 of 1,768 meeting near_silence opens came
   * on a mic that had already been loud, and 1,673 of them recovered.
   */
  private sampleLevel(): void {
    if (this.disposed || !this.measuring || this.measurement === null) return
    const { nearSilenceRMS, nearSilenceMS } = this.deps.config()
    const now = this.deps.now()
    const quiet = this.measurement.readLevel() < nearSilenceRMS
    if (quiet) {
      this.loudSamplesInRow = 0
      if (this.quietSince === null) this.quietSince = now
      const quietLongEnough = now - this.quietSince >= nearSilenceMS
      // Only a mic that reads ready: a digitally silent one already failed.
      const shouldFail =
        quietLongEnough && this.report.state === 'ready' && !this.isProven()
      if (shouldFail) {
        this.setReport({ state: 'failed', cause: 'near_silence' })
      }
    } else {
      this.quietSince = null
      this.loudSamplesInRow++
      if (this.loudSamplesInRow >= 2) {
        this.markProven()
        if (this.isFailedWith('near_silence')) this.setReadyReport()
      }
    }
    this.scheduleLevelSample()
  }

  private isProven(): boolean {
    if (this.trackProven) return true
    return this.deviceId !== '' && this.deps.provenDevices.has(this.deviceId)
  }

  private markProven(): void {
    this.trackProven = true
    if (this.deviceId !== '') this.deps.provenDevices.add(this.deviceId)
  }

  private isFailedWith(cause: MicFailure): boolean {
    return this.report.state === 'failed' && this.report.cause === cause
  }

  private setReadyReport(): void {
    this.setReport({
      state: 'ready',
      hasHeardSpeech: this.hasHeardSpeech,
      speechDetection: this.speechDetection,
    })
  }

  private refreshReadyReport(): void {
    if (this.report.state === 'ready') this.setReadyReport()
  }

  private onThresholdCross(gen: number, over: boolean): void {
    if (this.disposed || gen !== this.generation) return
    if (over) {
      this.speechStartedAt ??= this.deps.now()
      this.onCaptureSpeech()
    } else {
      this.endSpeech()
      this.speechCutByStop = false
    }
    if (!over || this.hasHeardSpeech) return
    this.hasHeardSpeech = true
    this.refreshReadyReport()
  }

  /**
   * capture_start only: speech heard between the silence reads proves the
   * capture works, so the remaining reads are cancelled. A student who talks
   * at second 2 and then listens must not fail on zeros read at 5, 10 and 15.
   * A capture that already failed recovers through the confirming reads.
   */
  private onCaptureSpeech(): void {
    const proves =
      this.deps.silenceCheck === 'capture_start' &&
      !this.captureMadeSound &&
      this.report.state !== 'failed'
    if (!proves) return
    this.captureMadeSound = true
    this.silentReadsInRow = 0
    if (this.pollTimer !== null) {
      this.deps.clearTimer(this.pollTimer)
      this.pollTimer = null
    }
    this.setReadyReport()
  }

  /**
   * Without this, a context stopped mid-utterance counted every second it
   * stayed stopped as speech. Stopping ends the speech in progress. On resume
   * the detector still counts the student as speaking and sends no new start,
   * so counting picks up again from the resume; if they went quiet while it
   * was stopped, its end of speech arrives within a moment.
   */
  private onAudioRunningChange(running: boolean): void {
    if (!running) {
      // A second stop (suspended, then interrupted) keeps the earlier cut.
      if (this.speechStartedAt !== null) this.speechCutByStop = true
      this.endSpeech()
      return
    }
    if (this.speechCutByStop && this.measuring) {
      this.speechStartedAt = this.deps.now()
    }
    this.speechCutByStop = false
  }

  private endSpeech(): void {
    if (this.speechStartedAt === null) return
    this.speechMS += this.deps.now() - this.speechStartedAt
    this.speechStartedAt = null
  }

  private teardownMeasurement(): void {
    this.measuring = false
    // Muting part-way through a capture-start check reads it again from the
    // start on unmute.
    this.silentReadsInRow = 0
    this.pendingRecoveryConfirm = false
    // The detector stops with the measurement, so speech in progress ends here.
    this.endSpeech()
    this.speechCutByStop = false
    if (this.pollTimer !== null) {
      this.deps.clearTimer(this.pollTimer)
      this.pollTimer = null
    }
    if (this.levelTimer !== null) {
      this.deps.clearTimer(this.levelTimer)
      this.levelTimer = null
    }
    // Muting or swapping the track starts the quiet period over.
    this.quietSince = null
    this.loudSamplesInRow = 0
    if (this.measurement) {
      this.measurement.cleanup()
      this.measurement = null
    }
    if (this.speechCleanup) {
      void this.speechCleanup()
      this.speechCleanup = null
    }
  }

  private clearGraceTimer(): void {
    if (this.graceTimer !== null) {
      this.deps.clearTimer(this.graceTimer)
      this.graceTimer = null
    }
  }
}
